import React, { useEffect, useMemo, useState } from "react";
import {
  FaEnvelope,
  FaEye,
  FaEyeSlash,
  FaInfoCircle,
  FaKey,
  FaUser,
} from "react-icons/fa";
import { fetchSignInMethodsForEmail } from "firebase/auth";
import { useAuth } from "../AuthContext";
import { firebaseAuth } from "../services/firebase";
import "./LoginStyles.css";

type LoginProps = {
  isOpen: boolean;
  onClose?: () => void;
};

type LoginMode = "login" | "register";

const REMEMBER_PASSWORD_KEY = "dear_language_login_remember_password";
const REMEMBER_EMAIL_KEY = "dear_language_login_email";
const REMEMBER_PASSWORD_VALUE_KEY = "dear_language_login_password";

const DISALLOWED_EMAIL_DOMAINS = new Set([
  "mailinator.com",
  "tempmail.com",
  "temp-mail.org",
  "10minutemail.com",
  "guerrillamail.com",
  "yopmail.com",
  "trashmail.com",
  "fakeinbox.com",
  "getnada.com",
  "emailondeck.com",
]);

const COMMON_EMAIL_DOMAIN_FIXES: Record<string, string> = {
  "gamil.com": "gmail.com",
  "gmial.com": "gmail.com",
  "gmail.con": "gmail.com",
  "gmail.co": "gmail.com",
  "hotmial.com": "hotmail.com",
  "hotmai.com": "hotmail.com",
  "outlok.com": "outlook.com",
  "outlook.con": "outlook.com",
  "yaho.com": "yahoo.com",
  "yahoo.con": "yahoo.com",
};

const safeGetStorageItem = (key: string): string => {
  try {
    if (typeof window === "undefined" || !window.localStorage) {
      return "";
    }

    return window.localStorage.getItem(key) || "";
  } catch {
    return "";
  }
};

const safeSetStorageItem = (key: string, value: string): void => {
  try {
    if (typeof window === "undefined" || !window.localStorage) {
      return;
    }

    window.localStorage.setItem(key, value);
  } catch {
    // Browser storage can be blocked.
  }
};

const safeRemoveStorageItem = (key: string): void => {
  try {
    if (typeof window === "undefined" || !window.localStorage) {
      return;
    }

    window.localStorage.removeItem(key);
  } catch {
    // Browser storage can be blocked.
  }
};

const getEmailDomain = (emailValue: string): string => {
  return emailValue.split("@")[1]?.trim().toLowerCase() || "";
};

const getFriendlyAuthError = (error: string): string => {
  const cleanError = error.trim();

  if (!cleanError) {
    return "";
  }

  const lowerError = cleanError.toLowerCase();

  if (
    lowerError.includes("auth/user-not-found") ||
    lowerError.includes("user-not-found") ||
    lowerError.includes("auth/invalid-credential") ||
    lowerError.includes("invalid-credential") ||
    lowerError.includes("auth/wrong-password") ||
    lowerError.includes("wrong-password")
  ) {
    return "E-Mail-Adresse oder Passwort ist falsch. Wenn Sie noch kein Konto haben, registrieren Sie sich bitte zuerst.";
  }

  if (
    lowerError.includes("auth/invalid-email") ||
    lowerError.includes("invalid-email")
  ) {
    return "Bitte geben Sie eine gültige E-Mail-Adresse ein.";
  }

  if (
    lowerError.includes("auth/email-already-in-use") ||
    lowerError.includes("email-already-in-use")
  ) {
    return "Diese E-Mail-Adresse ist bereits registriert. Bitte loggen Sie sich ein.";
  }

  if (
    lowerError.includes("auth/too-many-requests") ||
    lowerError.includes("too-many-requests")
  ) {
    return "Zu viele Versuche. Bitte warten Sie kurz und versuchen Sie es erneut.";
  }

  if (
    lowerError.includes("auth/network-request-failed") ||
    lowerError.includes("network-request-failed")
  ) {
    return "Die Verbindung ist fehlgeschlagen. Bitte prüfen Sie Ihr Internet.";
  }

  return cleanError;
};

const Login: React.FC<LoginProps> = ({ isOpen, onClose }) => {
  const { isLoggedIn, login, register, authError, clearAuthError } = useAuth();

  const [mode, setMode] = useState<LoginMode>("login");
  const [displayName, setDisplayName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [rememberPassword, setRememberPassword] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [localError, setLocalError] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (!isOpen) {
      setMode("login");
      setDisplayName("");
      setLocalError("");
      setShowPassword(false);
      clearAuthError();
      return;
    }

    const remembered = safeGetStorageItem(REMEMBER_PASSWORD_KEY) === "true";
    const savedEmail = safeGetStorageItem(REMEMBER_EMAIL_KEY);
    const savedPassword = safeGetStorageItem(REMEMBER_PASSWORD_VALUE_KEY);

    setRememberPassword(remembered);

    if (remembered) {
      setEmail(savedEmail);
      setPassword(savedPassword);
    }
  }, [isOpen, clearAuthError]);

  useEffect(() => {
    if (isLoggedIn && isOpen) {
      setDisplayName("");
      setLocalError("");
      setShowPassword(false);
      clearAuthError();

      if (!rememberPassword) {
        setEmail("");
        setPassword("");
      }

      if (onClose) {
        onClose();
      }
    }
  }, [isLoggedIn, isOpen, onClose, clearAuthError, rememberPassword]);

  const emailDomainSuggestion = useMemo(() => {
    const cleanEmail = email.trim().toLowerCase();
    const domain = getEmailDomain(cleanEmail);

    if (!domain) {
      return "";
    }

    const suggestedDomain = COMMON_EMAIL_DOMAIN_FIXES[domain];

    if (!suggestedDomain) {
      return "";
    }

    return cleanEmail.replace(`@${domain}`, `@${suggestedDomain}`);
  }, [email]);

  if (!isOpen) {
    return null;
  }

  const saveRememberedLogin = (
    cleanEmail: string,
    cleanPassword: string,
  ): void => {
    if (!rememberPassword || mode !== "login") {
      safeRemoveStorageItem(REMEMBER_PASSWORD_KEY);
      safeRemoveStorageItem(REMEMBER_EMAIL_KEY);
      safeRemoveStorageItem(REMEMBER_PASSWORD_VALUE_KEY);
      return;
    }

    safeSetStorageItem(REMEMBER_PASSWORD_KEY, "true");
    safeSetStorageItem(REMEMBER_EMAIL_KEY, cleanEmail);
    safeSetStorageItem(REMEMBER_PASSWORD_VALUE_KEY, cleanPassword);
  };

  const validateEmail = (cleanEmail: string): boolean => {
    if (!/\S+@\S+\.\S+/.test(cleanEmail)) {
      setLocalError("Bitte geben Sie eine gültige E-Mail-Adresse ein.");
      return false;
    }

    if (cleanEmail.includes(" ")) {
      setLocalError("Die E-Mail-Adresse darf keine Leerzeichen enthalten.");
      return false;
    }

    const domain = getEmailDomain(cleanEmail);

    if (!domain || !domain.includes(".")) {
      setLocalError("Bitte geben Sie eine gültige E-Mail-Adresse ein.");
      return false;
    }

    if (DISALLOWED_EMAIL_DOMAINS.has(domain)) {
      setLocalError(
        "Bitte verwenden Sie eine echte E-Mail-Adresse. Temporäre E-Mail-Adressen sind nicht erlaubt.",
      );
      return false;
    }

    if (COMMON_EMAIL_DOMAIN_FIXES[domain]) {
      setLocalError(
        `Meinten Sie ${cleanEmail.replace(
          `@${domain}`,
          `@${COMMON_EMAIL_DOMAIN_FIXES[domain]}`,
        )}? Bitte prüfen Sie Ihre E-Mail-Adresse.`,
      );
      return false;
    }

    return true;
  };

  const checkRegisterEmailAvailability = async (
    cleanEmail: string,
  ): Promise<boolean> => {
    try {
      const signInMethods = await fetchSignInMethodsForEmail(
        firebaseAuth,
        cleanEmail,
      );

      if (signInMethods.length > 0) {
        setLocalError(
          "Diese E-Mail-Adresse ist bereits registriert. Bitte loggen Sie sich ein.",
        );
        return false;
      }

      return true;
    } catch {
      return true;
    }
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();

    if (submitting) {
      return;
    }

    const cleanEmail = email.trim().toLowerCase();
    const cleanPassword = password.trim();
    const cleanDisplayName = displayName.trim();

    setLocalError("");
    clearAuthError();

    if (!validateEmail(cleanEmail)) {
      return;
    }

    if (cleanPassword.length < 6) {
      setLocalError("Das Passwort muss mindestens 6 Zeichen lang sein.");
      return;
    }

    if (mode === "register" && cleanDisplayName.length < 2) {
      setLocalError("Bitte geben Sie Ihren Namen ein.");
      return;
    }

    setSubmitting(true);

    if (mode === "register") {
      const emailAvailable = await checkRegisterEmailAvailability(cleanEmail);

      if (!emailAvailable) {
        setSubmitting(false);
        return;
      }
    }

    const success =
      mode === "login"
        ? await login(cleanEmail, cleanPassword)
        : await register(cleanEmail, cleanPassword, cleanDisplayName);

    setSubmitting(false);

    if (!success) {
      setLocalError(
        mode === "login"
          ? "E-Mail-Adresse oder Passwort ist falsch. Wenn Sie noch kein Konto haben, registrieren Sie sich bitte zuerst."
          : "Das Konto konnte nicht erstellt werden. Bitte prüfen Sie Ihre E-Mail-Adresse und versuchen Sie es erneut.",
      );
      return;
    }

    saveRememberedLogin(cleanEmail, cleanPassword);

    setDisplayName("");
    setShowPassword(false);

    if (!rememberPassword || mode === "register") {
      setEmail("");
      setPassword("");
    }

    if (onClose) {
      onClose();
    }
  };

  const handleClose = (): void => {
    setDisplayName("");
    setLocalError("");
    setShowPassword(false);
    clearAuthError();

    if (!rememberPassword) {
      setEmail("");
      setPassword("");
    }

    if (onClose) {
      onClose();
    }
  };

  const handleRememberPasswordChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const checked = event.target.checked;

    setRememberPassword(checked);

    if (!checked) {
      safeRemoveStorageItem(REMEMBER_PASSWORD_KEY);
      safeRemoveStorageItem(REMEMBER_EMAIL_KEY);
      safeRemoveStorageItem(REMEMBER_PASSWORD_VALUE_KEY);
    }
  };

  const applyEmailSuggestion = (): void => {
    if (!emailDomainSuggestion) {
      return;
    }

    setEmail(emailDomainSuggestion);
    setLocalError("");
    clearAuthError();
  };

  const handleModeToggle = (): void => {
    setMode(mode === "login" ? "register" : "login");
    setDisplayName("");
    setPassword("");
    setShowPassword(false);
    setLocalError("");
    clearAuthError();
  };

  const errorText = localError || getFriendlyAuthError(authError || "");
  const hasFeedback = Boolean(errorText || emailDomainSuggestion);

  return (
    <div className="login-modal-backdrop" role="presentation">
      <div
        className={`login-modal-card ${
          mode === "register" ? "login-modal-card-register" : ""
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-modal-title"
      >
        <div className="login-modal-particles" aria-hidden="true">
          <span />
          <span />
          <span />
          <span />
        </div>

        <button
          type="button"
          className="login-modal-close"
          onClick={handleClose}
          aria-label="Einloggen schließen"
        >
          ×
        </button>

        <div className="login-modal-badge">Premium-Zugang</div>

        <h2 id="login-modal-title" className="login-modal-title">
          {mode === "login"
            ? "Einloggen und weiterlernen"
            : "Konto erstellen"}
        </h2>

        <p className="login-modal-text">
          Mit einem Konto wird Ihr Zugang sicher gespeichert.
        </p>

        <div className="login-modal-note">
          <FaInfoCircle />
          <span>
            Für Kurse, Bücher und weitere Inhalte müssen Sie sich zuerst
            einloggen. Wenn Sie noch kein Konto haben, registrieren Sie sich
            bitte mit einer echten E-Mail-Adresse.
          </span>
        </div>

        <form onSubmit={handleSubmit} className="login-modal-form" noValidate>
          {mode === "register" && (
            <label className="login-modal-label">
              <span>Name</span>

              <div className="login-modal-input-shell">
                <FaUser className="login-modal-input-icon" />

                <input
                  type="text"
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                  placeholder="Ihr Name"
                  autoComplete="name"
                />
              </div>
            </label>
          )}

          <label className="login-modal-label">
            <span>E-Mail-Adresse</span>

            <div className="login-modal-input-shell">
              <FaEnvelope className="login-modal-input-icon" />

              <input
                type="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setLocalError("");
                  clearAuthError();
                }}
                placeholder="name@email.com"
                autoComplete="email"
                autoFocus
              />
            </div>
          </label>

          <label className="login-modal-label">
            <span>Passwort</span>

            <div className="login-modal-input-shell login-modal-password-shell">
              <FaKey className="login-modal-input-icon" />

              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  setLocalError("");
                  clearAuthError();
                }}
                autoComplete={
                  mode === "login" ? "current-password" : "new-password"
                }
              />

              <button
                type="button"
                className="login-modal-password-toggle"
                onClick={() => setShowPassword((current) => !current)}
                aria-label={
                  showPassword ? "Passwort ausblenden" : "Passwort anzeigen"
                }
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </label>

          <div
            className={`login-modal-feedback ${
              hasFeedback ? "login-modal-feedback-visible" : ""
            }`}
            aria-live="polite"
          >
            {emailDomainSuggestion ? (
              <button
                type="button"
                className="login-modal-suggestion"
                onClick={applyEmailSuggestion}
              >
                Meinten Sie <strong>{emailDomainSuggestion}</strong>?
              </button>
            ) : errorText ? (
              <p className="login-modal-error">{errorText}</p>
            ) : (
              <span className="login-modal-feedback-placeholder">
                Platzhalter
              </span>
            )}
          </div>

          {mode === "login" && (
            <label className="login-modal-remember">
              <input
                type="checkbox"
                checked={rememberPassword}
                onChange={handleRememberPasswordChange}
              />
              <span className="login-modal-check" />
              <span>Passwort merken</span>
            </label>
          )}

          <button
            type="submit"
            className="login-modal-button"
            disabled={submitting}
          >
            {submitting
              ? "Bitte warten..."
              : mode === "login"
                ? "Einloggen"
                : "Konto erstellen"}
          </button>
        </form>

        <button 
          type="button"
          className="login-modal-demo"
          onClick={handleModeToggle}
        >
          {mode === "login"
            ? "Noch kein Konto? Jetzt registrieren"
            : "Schon ein Konto? Einloggen"}
        </button>
      </div>
    </div>
  );
}; 

export default Login;